import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus, Plus } from 'lucide-react';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { SectionIntro } from '../components/alt/SectionIntro';
import { TALK_TO_SALES_URL } from '../lib/links';
import { ToolMarquee } from '../components/alt/ToolMarquee';
import { AgentFeatureMosaic } from '../components/aiagents/AgentFeatureMosaic';

/**
 * AI AGENTS — a purpose-built page, not the shared feature template.
 *
 * Every other Basics page runs through pages/FeatureDetail.tsx: name, promise,
 * a thesis, then a hairline-ruled specification. That shape is right for
 * Tracks or Groups, which are concepts. It is wrong for agents, because the
 * question a reader actually has is "what would one of these DO for me", and a
 * specification cannot answer it — only an artefact can.
 *
 * So this page is composed of product objects: a directory of agents, the
 * builder, a connections list, an approval prompt, a shared library. Each is
 * an idealised object rather than a screenshot (see agentArtefacts.tsx), and
 * three of them are paired with a photograph they break the frame of, so the
 * page reads warm rather than schematic.
 *
 * The template rendering of this same feature is preserved, unlinked, at
 * /features/ai-agents/alt.
 */

/** The CRT-head line-up: one owner per department, name tags and all. Composed
 * with the agents in the right half and open cream wall on the left, so it can
 * carry the whole hero with the copy lying in its negative space. */
const HERO_FIGURES = 'https://cdn.magicpatterns.com/uploads/oA3souZpK7RJcmK6E4qGis/hf_20260906_211514_1e585937-1c48-4023-b5e8-2dda8ac5b6c9.png';

/** The cut-out co-worker the home page closes on, so both endings match. */
const CLOSING_FIGURE = 'https://cdn.magicpatterns.com/uploads/3CtotG8s5CuCGowVgFZ3tH/Untitled_design_(10).png';
const HERO_POINTS = ['Start from a template built for your role', 'Connected to the tools you already run on', 'Nothing acts without your approval'];

/**
 * The definition, one interaction away. A reader who already knows what an
 * agent is should not have to scroll past a paragraph explaining it, and a
 * reader who does not should not have to guess — which is exactly what
 * progressive disclosure is for.
 */
function WhatIsAnAgent() {
  const [open, setOpen] = useState(false);
  return <div className="mt-7 max-w-[58ch]" data-id="element-7150" data-mp="2bgyqcoour4s2">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="group inline-flex items-center gap-2.5 text-[15px] font-bold text-[#2B211A] transition-colors duration-200 ease-out hover:text-[#EA580C]" data-id="element-7151" data-mp="2bfos9zi01wlf">
        <span aria-hidden="true" className="flex size-6 items-center justify-center rounded-full border border-[#E7E0D4] bg-white text-[#EA580C] transition-colors duration-200 ease-out group-hover:border-[#EA580C]" data-id="element-7152" data-mp="2ggumk1rb5rx2">
          {open ? <Minus size={13} strokeWidth={3} data-id="element-7153" data-mp="m3d42t5rfl8w" /> : <Plus size={13} strokeWidth={3} data-id="element-7154" data-mp="m4ei96i2onuu" />}
        </span>
        What is an AI agent?
      </button>
      {open && <p className="mt-4 border-l-2 border-[#EA580C] pl-4 text-[15.5px] leading-[1.7] text-[#6B5D52]" data-id="element-7155" data-mp="2xs04jzzj0z4l">
          A named agent with its own role, its own working context and its own memory. You
          describe the responsibility once — the brief before every renewal call, the Monday
          recap, the invoice chase — and it keeps doing it, on a schedule or on request, inside
          the tools your team already uses.
        </p>}
    </div>;
}
export function AIAgents() {
  return <>
      {/* ── Hero ──────────────────────────────────────────────────────────
       The line-up IS the hero's ground, not a column beside the copy. The
       frame is composed for it: the four agents stand in the right half and
       the left is open cream wall, so the copy lies in the picture's own
       negative space and the figures reach full height instead of being
       shrunk to fit a column. Its cream matches the page's, so the bleed to
       the viewport edges is seamless. */}
      <section className="relative overflow-hidden bg-[#FAF8F4] pb-16 pt-28 md:pb-20 md:pt-32" data-id="element-7156" data-mp="24a07kmuykjvb">
        <img src={HERO_FIGURES} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 size-full select-none object-cover object-[76%_100%] md:object-[62%_100%] lg:object-[54%_100%]" data-id="element-7157" data-mp="208enohk0b5bm" />

        {/* The wash, painted in the page's own cream so it reads as the
         picture's light falling off rather than a scrim on top of it.
         Vertical on phones, where the copy has to sit over the figures;
         horizontal from md up, where it only lifts the open wall. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(250,248,244,0.95)_0%,rgba(250,248,244,0.9)_52%,rgba(250,248,244,0.3)_100%)] md:bg-[linear-gradient(to_right,rgba(250,248,244,0.97)_0%,rgba(250,248,244,0.88)_38%,rgba(250,248,244,0.35)_56%,rgba(250,248,244,0)_72%)]" data-id="element-7158" data-mp="1cl6h7waudq80" />

        {/* Copy in the left half only. Nothing in the right — that is where the
         agents stand. */}
        {/* Copy in the left column, at its own measure. The height comes from
         the copy plus padding, NOT from a tall min-height on top of a tall
         top padding — those two were stacking into a ~930px hero. */}
        {/* `container mx-auto px-6` — the SAME container and gutter as the site
         header and the home page's opening statement, so the headline's left
         edge sits exactly under the Giga mark. The narrower max-w-6xl with a
         wider md:px-10 gutter is what made this hero's copy sit inboard of
         every other first screen on the site. */}
        <div className="container relative mx-auto grid min-h-[480px] grid-cols-1 items-center gap-12 px-6 md:min-h-[600px] md:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)] md:gap-8" data-id="element-7159" data-mp="ji5gvtpz81zj">
          <SectionIntro align="left" className="max-w-[34rem]" data-id="element-7160" data-mp="3i6bb0m2lymlz">
            {/* Group, then this page. */}
            <div className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.16em] text-[#A2917F]" data-id="element-7161" data-mp="3f6j9eq77oqs4">
              <span data-id="element-7162" data-mp="3a7zlik9254x4">Features</span>
              <span aria-hidden="true" className="h-px w-5 bg-[#D9CFBE]" data-id="element-7163" data-mp="3a6ofsew1xn3d" />
              <span className="text-[#EA580C]" data-id="element-7164" data-mp="3owm1mo6ie0pe">AI agents</span>
            </div>

            {/* One claim, one colour. The two-tone version had to name the
             category before it could make the promise; the breadcrumb above
             already says AI agents, so the headline can just be the promise.
             The terminating stop takes the accent, as everywhere on the site. */}
            {/* 68px in a ~520px column ran to four lines. 56px holds it to
             two and keeps the ticks, CTA and offer line inside the frame. */}
            <h1 className="font-title mt-6 text-[40px] font-bold leading-[1.05] tracking-[-0.03em] text-[#2B211A] sm:text-[50px] md:text-[56px]" data-id="element-7165" data-mp="318uqhnwpftpp">
              Give recurring work an owner.
            </h1>

            {/* Three ticks doing objection work in the first screen — fast,
             connected, safe — rather than making the reader find those
             answers 800px down the page. */}
            <ul className="mt-8 space-y-3" data-id="element-7166" data-mp="sx224hx6lyqm">
              {HERO_POINTS.map(point => <li key={point} className="flex items-start gap-3 text-[16px] text-[#4A3D33]" data-id="element-7167" data-mp="f0y9gt1yhyg9">
                  <Check size={17} strokeWidth={3} className="mt-[3px] flex-none text-[#EA580C]" data-id="element-7168" data-mp="j1qm7wa1elha" />
                  {point}
                </li>)}
            </ul>

            <WhatIsAnAgent data-id="element-7169" data-mp="1bq5vs3aegbl8" />

            <div className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" data-id="element-7170" data-mp="1bobagnv7uhgm">
              <WaitlistCTA analyticsLabel="ai-agents-hero" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:bg-[#C2410C]" data-id="element-7171" data-mp="xxg8zwn1r2n2">
                Get started free <ArrowRight size={16} data-id="element-7172" data-mp="1ufvsivxq37fs" />
              </WaitlistCTA>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full border border-[#E7E0D4] bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-colors duration-200 ease-out hover:border-[#D9CFBE] hover:bg-[#FFF8F3]" data-id="element-7173" data-mp="1ui8hvu5t8k2v">
                Talk to sales
              </a>
            </div>
            <p className="mt-6 text-[13.5px] text-[#A2917F]" data-id="element-7174" data-mp="r24zv4ogu61w">
              $100 in credits free · No card required
            </p>
          </SectionIntro>

          {/* The line-up, not a stock photograph and not a UI card.
            A woman at a laptop illustrated the READER, which is the one thing
            this headline is not about — it is about the work having an owner.
            These are the owners, each wearing the name of the function it
            answers to, the same taxonomy the category chips use further down.
            No card over it: the beats immediately below are ALL product
            surfaces, so the hero's job is the idea, not another screenshot.
            The artwork ships on white and is composited with multiply rather
            than framed in a panel — on cream the white multiplies away and the
            greys survive, so the figures stand ON the paper instead of inside
            a box that would cut them off at the knees. */}
        </div>
      </section>

      {/* ── One argument, composed as a board ─────────────────────────────
       The homepage does not turn every proof point into a full-width section.
       It introduces one argument, then places the evidence in an asymmetric
       board sized by importance. This chapter now follows that actual rule:
       predictable outer cards, with each card's internal composition chosen
       for the shape of its evidence. */}
      <section className="border-t border-[#EDE6DA] bg-white py-20 md:py-28" data-id="element-7175" data-mp="18a5vqqj9jwuh">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-7176" data-mp="18c8vw3e4r6cx">
          <AgentFeatureMosaic data-id="element-7177" data-mp="1d9sax5injn5x" />
        </div>
      </section>

      {/* ── What an agent reaches ─────────────────────────────────────────
       The directory, at the end rather than the beginning: breadth is only
       persuasive once the reader knows what the thing does with it. */}
      {/* The home page's tool band, not a grid of ten tiles.
        A fixed 5 × 2 grid of bordered white boxes ANSWERS the claim wrongly: it
        says the directory is exactly these ten, each equally worth naming, and
        it spends ten boxes and ten labels doing it — nine other brands' logo
        colours at full saturation, louder than anything else on the page. The
        band says the opposite by moving and by fading off both edges: this is a
        strip of a much longer list. It is also already the site's own idiom for
        exactly this claim, on ink, with the marks flattened to white
        silhouettes so the row reads as our directory rather than a collage. */}
      <section className="relative overflow-hidden bg-[#1A120C] py-20 md:py-28" data-id="element-7178" data-mp="14so504bgh5m">
        <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-7179" data-mp="1lnct043tps77" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-7180" data-mp="1go8npok0sem5">
          <SectionIntro align="left" className="max-w-[52ch]" data-id="element-7181" data-mp="1gquqzbzvo7yk">
            <h2 className="font-title text-[32px] font-bold leading-[1.1] tracking-[-0.03em] text-[#FAF8F4] md:text-[44px]" data-id="element-7182" data-mp="1cngc4onodxmp">
              Bring your work knowledge with you.
            </h2>
            <p className="mt-5 text-[16.5px] leading-[1.7] text-[#C9BFB4] md:text-[17.5px]" data-id="element-7183" data-mp="24ct05aa0kiwl">
              Connect Slack, Salesforce, Notion, Drive, your databases and hundreds more. Your
              agents work from real context instead of a generic prompt.
            </p>
          </SectionIntro>
        </div>

        {/* Full width, outside the container: the band's edge fade only reads
         as "continues past the frame" if the frame is the viewport. */}
        <div className="relative mt-14" data-id="element-7184" data-mp="2jatnvh0qnofn">
          <ToolMarquee data-id="element-7185" data-mp="2j9sa34sc8nu8" />
        </div>

        <div className="relative mx-auto mt-14 max-w-6xl px-6 md:px-10" data-id="element-7186" data-mp="327lw41sw7322">
          {/* The dark-panel action pattern used everywhere else on the site —
           one white pill — rather than an orange text link, which was the only
           place a bare coloured link stood in for a primary action. */}
          <Link to="/integrations" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-7187" data-mp="2xgncotclxoul">
            Explore integrations
            <ArrowRight size={16} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-7188" data-mp="2agfrp9652j6z" />
          </Link>
        </div>
      </section>

      {/* ── Close ─────────────────────────────────────────────────────────
       The home page's closing composition: an INSET ink card on cream with the
       cut-out co-worker anchored to its bottom-right and breaking the top
       edge. The full-bleed video plate was a different object on every count —
       edge to edge rather than a card, a cropped frame rather than a cut-out —
       and a crop of a figure can never do what a cut-out breaking the frame
       does, because the panel's own edge always ends up cutting the body. */}
      <section className="relative overflow-hidden bg-[#FAF8F4] pb-20 md:pb-28" data-id="element-7189" data-mp="2279yipf6fkcy">
        <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.5]" data-id="element-7190" data-mp="2gwo6bt33ttq8" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-7191" data-mp="2bxv44wt9pp5d">
          {/* The top margin is CLEARANCE FOR THE FIGURE, not section spacing. */}
          <div className="relative mt-24 rounded-[24px] bg-[#1A120C] px-6 py-12 shadow-[0_40px_100px_-45px_rgba(43,33,26,0.7)] sm:rounded-[28px] sm:px-10 sm:py-14 md:mt-32 md:px-14 md:py-20 lg:mt-40" data-id="element-7192" data-mp="2pwuk7g2psvpx">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[24px] sm:rounded-[28px]" data-id="element-7193" data-mp="vjv4zo28y3j6">
              <div className="dot-matrix-dark absolute inset-0 opacity-40" data-id="element-7194" data-mp="syx7sc7icrfi" />
            </div>

            {/* Sized by HEIGHT and stood on the panel's bottom edge, so all of
             the overflow lands at the TOP where the break-out reads. */}
            <img src={CLOSING_FIGURE} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-[-18px] right-[-6px] z-10 hidden h-[470px] w-auto max-w-none select-none lg:block xl:h-[520px]" data-id="element-7195" data-mp="120wpz7u9e1k1" />

            <div className="relative lg:pr-[380px] xl:pr-[440px]" data-id="element-7196" data-mp="9cisquazv99j">
              <SectionIntro align="left" data-id="element-7197" data-mp="e998thmsu4uy">
                <h2 className="font-title text-[32px] font-bold leading-[1.06] tracking-[-0.03em] text-[#FAF8F4] md:text-[48px]" data-id="element-7198" data-mp="e7y33c9smn17">
                  Put an agent on it this week.
                </h2>
                <p className="mt-5 max-w-[46ch] text-[16.5px] leading-[1.7] text-[#C9BFB4]" data-id="element-7199" data-mp="o1x242eklqox">
                  Pick one job that happens every week, hand it over, and see what comes back.
                </p>
                <div className="mt-9 flex flex-col items-stretch gap-x-8 gap-y-4 sm:flex-row sm:items-center" data-id="element-7200" data-mp="3rmugupdv50fo">
                  <WaitlistCTA analyticsLabel="ai-agents-close" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-[15px] font-bold text-[#2B211A] shadow-[0_14px_30px_-14px_rgba(0,0,0,0.6)] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7] sm:py-3.5" data-id="element-7201" data-mp="3f6bi1ghywq2s">
                    Get started free <ArrowRight size={16} data-id="element-7202" data-mp="3183h6xht94mc" />
                  </WaitlistCTA>
                  <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:text-white/70" data-id="element-7203" data-mp="2wesftbvrpg1t">
                    Talk to sales
                  </a>
                </div>
                <p className="mt-7 text-[13px] text-white/45" data-id="element-7204" data-mp="2hh9ktrshy6lx">
                  $100 in credits included · no card needed
                </p>
              </SectionIntro>

              {/* The figure appears on phones too, seated on the panel's bottom
               edge rather than breaking out — hidden below lg the panel had no
               character at all. */}
              <img src={CLOSING_FIGURE} alt="" aria-hidden="true" className="pointer-events-none mx-auto -mb-12 mt-10 block h-[210px] w-auto max-w-none select-none sm:-mb-14 sm:h-[250px] lg:hidden" data-id="element-7205" data-mp="1tvphgkioavfe" />

              <div className="relative mt-12 border-t border-white/10 pt-6 lg:mt-14" data-id="element-7206" data-mp="2mjl8b8lk9t49">
                <Link to="/features/pulses" className="group inline-flex items-center gap-2 text-[14px] font-bold text-white/55 transition-colors duration-200 ease-out hover:text-white" data-id="element-7207" data-mp="2ql6pv791ivks">
                  Next: Pulses
                  <ArrowRight size={15} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-7208" data-mp="2hsbnqmevdfz8" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}